import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const toISO = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const prettyDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return `${DOW[d.getDay()]}, ${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  let hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  hr = hr % 12 || 12;
  return `${hr}:${m || '00'} ${ampm}`;
};
const timeWindow = (a) => {
  const s = fmtTime(a.start_time);
  const e = fmtTime(a.end_time);
  if (a.all_day) return 'All day';
  if (s && e) return `${s} – ${e}`;
  return s || e || '';
};
const payorBadge = (a) => {
  const pk = (a.payor_kind || a.payor_type || '').toLowerCase();
  if (pk === 'warranty') return 'HSP / WARRANTY';
  if (pk === 'commercial') return 'COMMERCIAL';
  if (pk === 'construction') return 'CONSTRUCTION';
  return '';
};
const isCancelled = (a) => ['cancelled', 'canceled'].includes((a.status || '').toLowerCase());
const isCompleted = (a) => (a.status || '').toLowerCase() === 'completed' || !!a.actual_end_time;

const DEFAULT_OPTS = {
  phones: true,
  addresses: true,
  internalNotes: true,
  unscheduled: true,
  cancelled: false,
  completed: true,
  groupByTech: false, // default = group by day, then tech
};

const JobRow = ({ a, opts, techName }) => {
  const badge = payorBadge(a);
  return (
    <div className="sp-job">
      <div className="sp-job-head">
        <span className="sp-time">{timeWindow(a) || '—'}</span>
        <span className="sp-title">{a.shift_title || a.service_type || 'Job'}</span>
        {badge && <span className="sp-badge">{badge}</span>}
        {a.move_up_requested && <span className="sp-badge sp-badge-amber">MOVE-UP</span>}
        {isCancelled(a) && <span className="sp-badge sp-badge-red">CANCELLED</span>}
        {isCompleted(a) && !isCancelled(a) && <span className="sp-badge sp-badge-green">DONE</span>}
      </div>
      <div className="sp-job-body">
        {techName && <div><b>Tech:</b> {techName}</div>}
        {a.customer_name && <div><b>Customer:</b> {a.customer_name}</div>}
        {opts.addresses && a.address && <div><b>Address:</b> {a.address}</div>}
        {opts.phones && a.customer_phone && <div><b>Phone:</b> {a.customer_phone}</div>}
        {a.work_order_number && <div><b>WO#:</b> {a.work_order_number}</div>}
        {Array.isArray(a.helpers) && a.helpers.length > 0 && (
          <div><b>Helpers:</b> {a.helpers.map((h) => h.name || h).join(', ')}</div>
        )}
        {opts.internalNotes && (a.dispatcher_notes || a.notes) && (
          <div className="sp-notes"><b>Notes:</b> {a.dispatcher_notes || a.notes}</div>
        )}
      </div>
    </div>
  );
};

const DaySection = ({ iso, appts, employees, opts }) => {
  const empName = useMemo(() => {
    const m = {};
    (employees || []).forEach((e) => { m[e.id] = e.name; });
    return m;
  }, [employees]);
  let list = appts.filter((a) => a.date === iso);
  if (!opts.cancelled) list = list.filter((a) => !isCancelled(a));
  if (!opts.completed) list = list.filter((a) => !isCompleted(a));

  // Group by tech within the day
  const byTech = {};
  list.forEach((a) => {
    const key = a.employee_id || '__unassigned__';
    if (!byTech[key]) byTech[key] = [];
    byTech[key].push(a);
  });
  const techKeys = Object.keys(byTech).sort((x, y) => {
    if (x === '__unassigned__') return 1;
    if (y === '__unassigned__') return -1;
    return (empName[x] || '').localeCompare(empName[y] || '');
  });
  Object.values(byTech).forEach((arr) => arr.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));

  return (
    <div className="sp-day">
      <h2 className="sp-day-title">{prettyDate(iso)} <span className="sp-count">({list.length} job{list.length === 1 ? '' : 's'})</span></h2>
      {list.length === 0 ? (
        <p className="sp-empty">No jobs scheduled.</p>
      ) : (
        techKeys.map((tk) => (
          <div key={tk} className="sp-tech">
            <h3 className="sp-tech-title">{tk === '__unassigned__' ? 'Unassigned' : (empName[tk] || 'Unknown Tech')}</h3>
            {byTech[tk].map((a) => <JobRow key={a.id} a={a} opts={opts} />)}
          </div>
        ))
      )}
    </div>
  );
};

const SchedulePrintDialog = ({ open, onOpenChange, appointments, employees, weekDays, weekLabel }) => {
  const [scope, setScope] = useState('week'); // 'day' | 'week'
  const todayISO = toISO(new Date());
  const [dayISO, setDayISO] = useState(todayISO);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [onCall, setOnCall] = useState(null);
  const [drafts, setDrafts] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/on-call/today`, { withCredentials: true }).then((r) => setOnCall(r.data || {})).catch(() => setOnCall({}));
    axios.get(`${API}/admin/unscheduled-work`, { withCredentials: true }).then((r) => setDrafts(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => setDrafts([]));
  }, [open]);

  const weekISO = useMemo(() => (weekDays || []).map((d) => toISO(d)), [weekDays]);

  const toggle = (k) => setOpts((p) => ({ ...p, [k]: !p[k] }));

  const doPrint = () => {
    document.body.classList.add('nf-print-schedule');
    const cleanup = () => {
      document.body.classList.remove('nf-print-schedule');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    setTimeout(cleanup, 4000);
    setTimeout(() => window.print(), 60);
  };

  const daysToRender = scope === 'day' ? [dayISO] : weekISO;
  const rangeLabel = scope === 'day' ? prettyDate(dayISO) : (weekLabel || `${weekISO[0]} – ${weekISO[6]}`);
  const deptEntries = onCall ? Object.entries(onCall) : [];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="schedule-print-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Printer className="w-5 h-5 text-[#D4AF37]" /> Print / Export Schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Scope</Label>
              <div className="flex gap-2 mt-1">
                <button data-testid="sp-scope-day" onClick={() => setScope('day')} className={`flex-1 h-9 rounded border text-sm ${scope === 'day' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#262626] text-white'}`}>Single Day</button>
                <button data-testid="sp-scope-week" onClick={() => setScope('week')} className={`flex-1 h-9 rounded border text-sm ${scope === 'week' ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#262626] text-white'}`}>Current Week</button>
              </div>
              {scope === 'day' && (
                <input data-testid="sp-day-picker" type="date" value={dayISO} onChange={(e) => setDayISO(e.target.value)} className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] rounded h-9 px-2 text-sm text-white" />
              )}
            </div>

            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Include</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[
                  ['phones', 'Phone numbers'],
                  ['addresses', 'Addresses'],
                  ['internalNotes', 'Internal notes'],
                  ['unscheduled', 'Unscheduled jobs'],
                  ['completed', 'Completed jobs'],
                  ['cancelled', 'Cancelled jobs'],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input data-testid={`sp-opt-${k}`} type="checkbox" checked={!!opts[k]} onChange={() => toggle(k)} className="accent-[#D4AF37]" />
                    <span className={opts[k] ? 'text-white' : 'text-[#737373]'}>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button data-testid="sp-print-btn" onClick={doPrint} className="w-full h-11 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
              <Printer className="w-4 h-4 mr-2" /> Print {scope === 'day' ? 'Daily' : 'Weekly'} Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print-only layout */}
      <div id="schedule-print" style={{ display: 'none' }}>
        <div className="sp-header">
          <div>
            <h1>NEXFLOW INC.</h1>
            <p className="sp-sub">PREMIUM PLUMBING &amp; DRAIN CLEANING</p>
          </div>
          <div className="sp-header-right">
            <h2>{scope === 'day' ? 'DAILY SCHEDULE' : 'WEEKLY SCHEDULE'}</h2>
            <p>{rangeLabel}</p>
          </div>
        </div>

        {deptEntries.length > 0 && (
          <div className="sp-oncall">
            <b>On Call:</b>{' '}
            {deptEntries.map(([dept, info], i) => (
              <span key={dept}>
                {i > 0 ? ' · ' : ''}{(dept === 'new_construction' ? 'New Construction' : dept.charAt(0).toUpperCase() + dept.slice(1))}: {info.user_name || 'Not Assigned'}
                {info.next?.user_name ? ` (next: ${info.next.user_name})` : ''}
              </span>
            ))}
          </div>
        )}

        {daysToRender.map((iso) => (
          <DaySection key={iso} iso={iso} appts={appointments || []} employees={employees} opts={opts} />
        ))}

        {opts.unscheduled && drafts.length > 0 && (
          <div className="sp-day sp-unscheduled">
            <h2 className="sp-day-title">Unscheduled / Holding Area <span className="sp-count">({drafts.length})</span></h2>
            {drafts.map((d) => (
              <div key={d.id} className="sp-job">
                <div className="sp-job-head">
                  <span className="sp-title">{d.title || d.service_type || 'Unscheduled job'}</span>
                  {d.priority && d.priority !== 'Normal' && <span className="sp-badge sp-badge-amber">{String(d.priority).toUpperCase()}</span>}
                </div>
                <div className="sp-job-body">
                  {(d.who || d.customer_name) && <div><b>Customer:</b> {d.who || d.customer_name}</div>}
                  {opts.addresses && d.address && <div><b>Address:</b> {d.address}</div>}
                  {opts.phones && d.customer_phone && <div><b>Phone:</b> {d.customer_phone}</div>}
                  {d.work_order_number && <div><b>WO#:</b> {d.work_order_number}</div>}
                  {opts.internalNotes && (d.dispatcher_notes || d.notes) && <div className="sp-notes"><b>Notes:</b> {d.dispatcher_notes || d.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="sp-footer">Nexflow Inc. · Generated {prettyDate(todayISO)}</div>
      </div>
    </>
  );
};

export default SchedulePrintDialog;
